import React, { useState, useEffect, useCallback } from "react";
import { Settings, Save, Eye, EyeOff, CheckCircle2, Info, ExternalLink, Video, Server, Users } from "lucide-react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { PageHeader, AppButton, Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import PlatformAccountPanel from "./PlatformAccountPanel";
import OrganizerPoolPanel from "./OrganizerPoolPanel";
import "../Settings/settings.scss";
import "./zoho-meeting-config.scss";

const ZOHO_REGIONS = [
  { value: "in", label: "India (.in)" },
  { value: "com", label: "US (.com)" },
  { value: "eu", label: "EU (.eu)" },
  { value: "au", label: "Australia (.com.au)" },
  { value: "jp", label: "Japan (.jp)" },
];

const DEFAULT_REDIRECT = "https://api.lmscampus.com/api/institute/zoho-meeting/oauth/callback";

const ZohoMeetingConfigPage = () => {
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);

  const [tab, setTab] = useState("oauth-app");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [platformConnected, setPlatformConnected] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
    redirectUri: DEFAULT_REDIRECT,
    defaultRegion: "in",
    hasClientSecret: false,
  });

  const refresh = async () => {
    const { data } = await apiClient.get("/sa/zoho-meeting");
    setForm({
      enabled: data.enabled || false,
      clientId: data.clientId || "",
      clientSecret: data.clientSecret || "",
      redirectUri: data.redirectUri || DEFAULT_REDIRECT,
      defaultRegion: data.defaultRegion || "in",
      hasClientSecret: data.hasClientSecret || false,
    });
  };

  const refreshPlatformConnected = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/sa/zoho-meeting/platform-account");
      setPlatformConnected(!!data?.connected);
    } catch {
      setPlatformConnected(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try { await refresh(); } catch { /* defaults */ }
      await refreshPlatformConnected();
      setLoading(false);
    })();
    const url = new URL(window.location.href);
    if (url.searchParams.get("platform_connected")) {
      setTab("account");
    }
  }, [refreshPlatformConnected]);

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const toggleSecret = (field) => setShowSecrets((p) => ({ ...p, [field]: !p[field] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/sa/zoho-meeting", {
        enabled: form.enabled,
        clientId: form.clientId,
        clientSecret: form.clientSecret,
        redirectUri: form.redirectUri,
        defaultRegion: form.defaultRegion,
      });
      showToast("Zoho Meeting settings saved!");
      await refresh();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save.", "error");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="d-flex justify-content-center p-4"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div className="sa-settings">
      <PageHeader
        title="Zoho Meeting Configuration"
        description="Configure platform-wide Zoho Meeting credentials, manage the LMS-owned account, and the organizer pool used to host classes for tenants."
        icon={Video}
      />

      <Tabs value={tab} onChange={setTab} className="zoho-meeting-tabs">
        <TabsList>
          <TabsTrigger value="oauth-app">
            <Settings size={14} className="me-1" /> OAuth App
          </TabsTrigger>
          <TabsTrigger value="account">
            <Server size={14} className="me-1" /> Platform Account
          </TabsTrigger>
          <TabsTrigger value="pool">
            <Users size={14} className="me-1" /> Organizer Pool
          </TabsTrigger>
        </TabsList>

        <TabsContent value="oauth-app">
          <div className="sa-card">
            <div className="sa-card-head">
              <Settings size={16} className="me-2" /> Zoho Meeting OAuth App Credentials
              <AppButton variant="primary" size="sm" onClick={handleSave} disabled={saving} className="zoho-meeting-cfg__save-btn">
                <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save"}
              </AppButton>
            </div>
            <div className="sa-card-body">
              <div className="zoho-meeting-cfg__hint">
                <Info size={13} className="me-1" />
                These OAuth credentials power BOTH the platform-owned Zoho account AND any institute that
                connects its own Zoho ("BYO" mode). Create a Server-based Application in your{" "}
                <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer">
                  Zoho API Console <ExternalLink size={11} />
                </a>{" "}
                and request the following scopes: <code>ZohoMeeting.meetings.CREATE</code>,{" "}
                <code>ZohoMeeting.meetings.READ</code>, <code>ZohoMeeting.meetings.UPDATE</code>,{" "}
                <code>ZohoMeeting.meetings.DELETE</code>, <code>ZohoMeeting.recordings.READ</code>,{" "}
                <code>ZohoMeeting.users.READ</code>, <code>AaaServer.profile.READ</code>.
              </div>

              <Row className="g-3">
                <Col md={12}>
                  <Form.Check
                    type="switch"
                    id="zoho-meeting-enabled"
                    label="Enable Zoho Meeting Integration"
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Client ID</Form.Label>
                    <Form.Control size="sm" value={form.clientId} onChange={update("clientId")} placeholder="e.g. 1000.XXXXXXXXXX.XXXXXXXXXX" />
                    <Form.Text className="text-muted zoho-meeting-cfg__sub">From Zoho API Console → Client Secret tab</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">
                      Client Secret
                      {form.hasClientSecret && <CheckCircle2 size={13} className="ms-1 text-success" />}
                    </Form.Label>
                    <div className="d-flex gap-1">
                      <Form.Control
                        size="sm"
                        type={showSecrets.clientSecret ? "text" : "password"}
                        value={form.clientSecret}
                        onChange={update("clientSecret")}
                        placeholder={form.hasClientSecret ? "Leave blank to keep existing" : "Enter client secret…"}
                      />
                      <button type="button" className="btn btn-sm btn-outline-secondary zoho-meeting-cfg__eye" onClick={() => toggleSecret("clientSecret")}>
                        {showSecrets.clientSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <Form.Text className="text-muted zoho-meeting-cfg__sub">Stored encrypted. Leave blank when editing to keep current value.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Redirect URI</Form.Label>
                    <Form.Control size="sm" value={form.redirectUri} onChange={update("redirectUri")} placeholder={DEFAULT_REDIRECT} />
                    <Form.Text className="text-muted zoho-meeting-cfg__sub">Must match the redirect URI registered in Zoho API Console</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Default Region</Form.Label>
                    <Form.Select size="sm" value={form.defaultRegion} onChange={update("defaultRegion")}>
                      {ZOHO_REGIONS.map(d => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted zoho-meeting-cfg__sub">Used as the default region for the platform account and BYO institutes</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="account">
          <div className="sa-card">
            <div className="sa-card-head">
              <Server size={16} className="me-2" /> Platform Zoho Account
            </div>
            <div className="sa-card-body">
              {!form.enabled || !form.hasClientSecret ? (
                <div className="zoho-meeting-cfg__warn">
                  <Info size={14} className="me-1" />
                  Enable Zoho Meeting and save OAuth credentials in the <strong>OAuth App</strong> tab before
                  connecting the platform account.
                </div>
              ) : (
                <PlatformAccountPanel showToast={showToast} defaultRegion={form.defaultRegion} />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pool">
          <div className="sa-card">
            <div className="sa-card-head">
              <Users size={16} className="me-2" /> Shared Organizer Pool
            </div>
            <div className="sa-card-body">
              <div className="zoho-meeting-cfg__hint">
                <Info size={13} className="me-1" />
                Each organizer in the pool is a sub-user of the platform Zoho account and can host one
                concurrent class. Per-institute concurrency caps are configured in the <strong>Plans</strong>
                page (<code>Max Concurrent Live Classes</code>).
              </div>
              <OrganizerPoolPanel showToast={showToast} platformConnected={platformConnected} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default ZohoMeetingConfigPage;
